import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, FileText, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `${(c || 0) < 0 ? '-' : ''}$${(Math.abs(c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const REPORTS = [
  ['pnl', 'Profit & Loss'],
  ['balance_sheet', 'Balance Sheet'],
  ['cash_flow', 'Cash Flow'],
  ['statement', 'Account Statement'],
];

const Row = ({ label, cents, detail, bold, neg }) => (
  <div className={`flex items-center justify-between py-1.5 px-3 ${bold ? 'font-bold border-t border-border bg-muted/20' : 'border-t border-border/50'}`}>
    <span className="text-sm">{label}{detail && <span className="text-[10px] text-muted-foreground ml-2">{detail}</span>}</span>
    <span className={`text-sm font-semibold ${neg || cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{usd(cents)}</span>
  </div>
);

export const BusinessReports = () => {
  const [report, setReport] = useState('pnl');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/platform-financials/reports`, { ...auth(), params: { days } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not build reports'))
      .finally(() => setLoading(false));
  }, [days]);

  const print = () => {
    const w = window.open('', '_blank');
    const title = REPORTS.find(([k]) => k === report)?.[1];
    w.document.write(`<html><head><title>Eventism — ${title}</title><style>
      body{font-family:Arial,sans-serif;max-width:720px;margin:32px auto;color:#111}
      h1{font-size:20px} h2{font-size:14px;color:#555} table{width:100%;border-collapse:collapse;font-size:13px}
      td,th{padding:6px 8px;border-bottom:1px solid #ddd;text-align:left} td:last-child,th:last-child{text-align:right}
      .tot{font-weight:bold;background:#f5f5f5}</style></head><body>
      <h1>Eventism — ${title}</h1><h2>${data.period.start} – ${data.period.end}</h2>
      ${printRef.current?.innerHTML.replace(/class="[^"]*"/g, '') || ''}
      </body></html>`);
    w.document.close();
    w.print();
  };

  const d = data;
  return (
    <div data-testid="business-reports-section">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <p className="text-sm font-semibold flex items-center gap-1.5"><FileText className="h-4 w-4 text-sky-400" /> Business Reports</p>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(parseInt(e.target.value))}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="reports-days-select">
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 12 months</option>
          </select>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={print} disabled={!d} data-testid="print-report-btn">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print / PDF
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {REPORTS.map(([k, label]) => (
          <button key={k} onClick={() => setReport(k)} data-testid={`report-tab-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${report === k ? 'bg-foreground text-background border-foreground font-semibold' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading || !d ? (
        <Card className="border-border"><CardContent className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></CardContent></Card>
      ) : (
        <Card className="border-border"><CardContent className="py-4 px-0" >
          <div ref={printRef}>
          <p className="text-[11px] text-muted-foreground px-3 pb-2">{d.period.start} — {d.period.end}</p>

          {report === 'pnl' && (
            <div data-testid="report-pnl">
              <p className="text-xs font-bold uppercase tracking-wider px-3 py-1 text-muted-foreground">Revenue</p>
              {d.pnl.revenue.map(r => <Row key={r.label} label={r.label} detail={r.detail} cents={r.cents} />)}
              <Row label="Total revenue" cents={d.pnl.revenue_total_cents} bold />
              <p className="text-xs font-bold uppercase tracking-wider px-3 py-1 mt-3 text-muted-foreground">Expenses</p>
              {d.pnl.expenses.map(r => <Row key={r.label} label={r.label} cents={-Math.abs(r.cents)} />)}
              <Row label="Total expenses" cents={-Math.abs(d.pnl.expenses_total_cents)} bold />
              <div className="mt-3"><Row label="Net profit" cents={d.pnl.net_profit_cents} bold /></div>
              <p className="text-[10px] text-muted-foreground px-3 pt-2">Context: {usd(d.pnl.gross_sales_cents)} gross ticket sales processed (pass-through, not revenue) · {usd(d.pnl.refunds_cents)} refunded.</p>
            </div>
          )}

          {report === 'balance_sheet' && (
            <div data-testid="report-balance-sheet">
              <p className="text-[11px] text-muted-foreground px-3 pb-1">As of {d.balance_sheet.as_of}</p>
              <p className="text-xs font-bold uppercase tracking-wider px-3 py-1 text-muted-foreground">Assets</p>
              {d.balance_sheet.assets.map(r => <Row key={r.label} label={r.label} cents={r.cents} />)}
              <Row label="Total assets" cents={d.balance_sheet.assets_total_cents} bold />
              <p className="text-xs font-bold uppercase tracking-wider px-3 py-1 mt-3 text-muted-foreground">Liabilities</p>
              {d.balance_sheet.liabilities.map(r => <Row key={r.label} label={r.label} cents={-Math.abs(r.cents)} />)}
              <Row label="Total liabilities" cents={-Math.abs(d.balance_sheet.liabilities_total_cents)} bold />
              <div className="mt-3"><Row label="Owner's equity (Platform Profits)" cents={d.balance_sheet.equity_cents} bold /></div>
            </div>
          )}

          {report === 'cash_flow' && (
            <div data-testid="report-cash-flow">
              <p className="text-xs font-bold uppercase tracking-wider px-3 py-1 text-muted-foreground">Operating activities</p>
              {d.cash_flow.operating.map(r => <Row key={r.label} label={r.label} detail={r.count ? `${r.count} transactions` : ''} cents={r.cents} />)}
              <Row label="Net cash from operations" cents={d.cash_flow.operating_total_cents} bold />
              <p className="text-xs font-bold uppercase tracking-wider px-3 py-1 mt-3 text-muted-foreground">Financing activities</p>
              {d.cash_flow.financing.map(r => <Row key={r.label} label={r.label} detail={r.count ? `${r.count} transactions` : ''} cents={r.cents} />)}
              <Row label="Net cash from financing" cents={d.cash_flow.financing_total_cents} bold />
              <div className="mt-3"><Row label="Net change in cash" cents={d.cash_flow.net_change_cents} bold /></div>
            </div>
          )}

          {report === 'statement' && (
            <div data-testid="report-statement">
              <div className="flex gap-4 px-3 pb-2 text-xs">
                <span>Total in: <strong className="text-emerald-400">{usd(d.statement.total_in_cents)}</strong></span>
                <span>Total out: <strong className="text-rose-400">{usd(d.statement.total_out_cents)}</strong></span>
                <span className="text-muted-foreground">{d.statement.transactions_scanned} transactions{d.statement.truncated ? ` (showing first ${d.statement.rows.length})` : ''}</span>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background"><tr className="text-left text-muted-foreground border-b border-border">
                    <th className="px-3 py-1.5">Date</th><th className="px-3 py-1.5">Description</th><th className="px-3 py-1.5">Category</th><th className="px-3 py-1.5 text-right">Amount</th><th className="px-3 py-1.5 text-right">Fee</th><th className="px-3 py-1.5 text-right">Net</th>
                  </tr></thead>
                  <tbody>
                    {d.statement.rows.map((r, i) => (
                      <tr key={i} className="border-b border-border/40">
                        <td className="px-3 py-1 whitespace-nowrap">{r.date}</td>
                        <td className="px-3 py-1">{r.description}</td>
                        <td className="px-3 py-1 capitalize text-muted-foreground">{r.category}</td>
                        <td className={`px-3 py-1 text-right font-semibold ${r.amount_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{usd(r.amount_cents)}</td>
                        <td className="px-3 py-1 text-right text-muted-foreground">{usd(-r.fee_cents)}</td>
                        <td className="px-3 py-1 text-right">{usd(r.net_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};
